// history.ts — shared REST history-fetch logic for both the guest widget
// (index.ts) and the agent dashboard (operate.ts).
//
// Strategy:
//   • On open: fetch the latest 20 messages. Fast, cheap, covers most chats.
//   • hasMore=true → show a sentinel div at the top of the scroll area.
//   • When the user scrolls near the top (scrollTop < 80px) fetch the next
//     20 older messages and prepend — no button click required.
//   • This is the same infinite-scroll-upward pattern used by WhatsApp/Telegram.

import type { ChatStore } from './store.js'
import type { Message, ConversationId } from './protocol/index.js'
import type { Renderer } from './renderer.js'

const PAGE = 20   // messages per fetch — fast first load, smooth pagination

/** Resolve a single user-supplied relay URL into the concrete WebSocket URL and
 *  HTTP(S) base the client needs.
 *
 *  Accepts any scheme — `https://`, `http://`, `wss://`, or `ws://` — and any of
 *  these shapes: bare origin (`https://api.example.com`), origin with `/ws`
 *  (`wss://api.example.com/ws`), or a sub-path (`https://api.example.com/relay`).
 *
 *    https://api.example.com      → ws  wss://api.example.com/ws   · http https://api.example.com
 *    wss://api.example.com/ws     → ws  wss://api.example.com/ws   · http https://api.example.com
 *    http://localhost:3000        → ws  ws://localhost:3000/ws     · http http://localhost:3000
 *
 *  `https`/`wss` map to a secure socket (`wss`); `http`/`ws` map to `ws`. A bare
 *  host with no scheme is assumed secure. Pass `apiBaseOverride` only when the
 *  REST API lives on a different origin than the socket. */
export function resolveRelayUrls(input: string, apiBaseOverride?: string): { wsUrl: string; httpBase: string } {
  const trimmed = input.trim().replace(/\/+$/, '')
  const scheme = trimmed.match(/^(https|http|wss|ws):\/\//)?.[1]
  const secure = scheme ? scheme === 'https' || scheme === 'wss' : true
  const authorityAndPath = (scheme ? trimmed.slice(scheme.length + 3) : trimmed).replace(/\/ws$/, '')
  const httpBase = apiBaseOverride
    ? apiBaseOverride.trim().replace(/\/+$/, '')
    : `${secure ? 'https' : 'http'}://${authorityAndPath}`
  const wsUrl = `${secure ? 'wss' : 'ws'}://${authorityAndPath}/ws`
  return { wsUrl, httpBase }
}

/** Derive the HTTP(S) base origin from a ws(s):// URL.
 *  @deprecated prefer {@link resolveRelayUrls}; kept for back-compat. */
export function httpBaseFromWsUrl(wsUrl: string): string {
  return resolveRelayUrls(wsUrl).httpBase
}

/** Build the best history URL for the given token context.
 *  Staff tokens use /messages (full access); guest tokens use /history. */
function historyUrl(httpBase: string, conversationId: string, beforeSeq: number, limit: number): string[] {
  const qs = `beforeSeq=${beforeSeq}&limit=${limit}`
  return [
    `${httpBase}/conversations/${conversationId}/messages?${qs}`,
    `${httpBase}/conversations/${conversationId}/history?${qs}`,
  ]
}

/** Fetch one page of history. Tries staff endpoint first, falls back to guest.
 *
 *  Returns `null` ONLY when every attempt failed. That case used to be
 *  indistinguishable from "no history" and was swallowed without a word:
 *  `catch { }` here, `if (!page) return` in the caller. When the REST calls
 *  were being CORS-rejected from a customer domain (the WebSocket is not
 *  subject to CORS, so live chat kept working) the visible symptom was
 *  "my messages disappear when I refresh" with NOTHING in the console to
 *  explain it. A transport failure is now reported. */
async function fetchPage(
  httpBase: string,
  conversationId: string,
  token: string,
  beforeSeq: number,
  limit = PAGE,
): Promise<{ messages: Message[]; hasMore: boolean } | null> {
  let lastError: unknown
  let sawResponse = false
  for (const url of historyUrl(httpBase, conversationId, beforeSeq, limit)) {
    try {
      const res = await fetch(url, { headers: { authorization: `Bearer ${token}` } })
      sawResponse = true            // reached the server; this endpoint just said no
      if (!res.ok) continue
      const data = await res.json() as { messages?: Message[]; hasMore?: boolean }
      return { messages: data.messages ?? [], hasMore: data.hasMore ?? false }
    } catch (e) { lastError = e }
  }
  // A thrown fetch (as opposed to an HTTP error) is a TRANSPORT failure —
  // overwhelmingly CORS, occasionally DNS/offline. Name it, because the user
  // just watched their history vanish.
  if (!sawResponse) {
    console.error(
      `[chat-widget] could not load history from ${httpBase} — the request never reached the server. `
      + 'This is almost always CORS: add this site\'s origin to the chatroom\'s allowed origins '
      + '(dashboard → chatroom → allowed origins) or to the server\'s CORS_ORIGINS.',
      lastError,
    )
  } else {
    console.error(`[chat-widget] history request to ${httpBase} was rejected for conversation ${conversationId}.`)
  }
  return null
}

/** Initial history restore on conversation open.
 *
 *  Fetches the latest PAGE messages and sets up scroll-triggered loading for
 *  older messages: a `scroll` listener on the container's own scrollTop (see
 *  below), not an IntersectionObserver sentinel. No buttons — scrolling up
 *  loads more automatically.
 *
 *  Does NOT return a cleanup function — the scroll-listener teardown is
 *  registered internally via `renderer.setScrollCleanup()` and runs whenever
 *  the renderer tears down the conversation view. Callers just `void` this
 *  call (see index.ts). */
export async function restoreHistory(
  wsUrl: string,
  token: string,
  conversationId: ConversationId,
  store: ChatStore,
  renderer: Renderer,
  apiBase?: string,
): Promise<void> {
  const httpBase = apiBase ? apiBase.replace(/\/+$/, '') : httpBaseFromWsUrl(wsUrl)

  const page = await fetchPage(httpBase, conversationId as string, token, Number.MAX_SAFE_INTEGER)
  if (!page) return

  if (page.messages.length) {
    store.apply({ type: 'sync', conversationId, messages: page.messages })
    // Always set hasMore from the response
    store.apply({ type: 'history', conversationId, messages: [], hasMore: page.hasMore })
    renderer.render(store)
  } else {
    // No messages — still record hasMore=false so the sentinel doesn't show
    store.apply({ type: 'history', conversationId, messages: [], hasMore: false })
  }

  if (!page.hasMore) return

  // ── Scroll-triggered load-more ────────────────────────────────────────────
  // The renderer shows a sentinel div ("↑ Loading earlier messages…") at the
  // top of the scroll area whenever hasMoreHistory is true (visual only, not
  // observed) — the trigger is this scroll listener on the container itself:
  // when scrollTop < 80px, load more.
  let loading = false

  const loadOlder = async () => {
    if (loading || !store.hasMoreHistory) return
    loading = true
    const oldest = store.messages()[0]
    if (!oldest) { loading = false; return }
    const page2 = await fetchPage(httpBase, conversationId as string, token, oldest.seq)
    if (page2) {
      store.apply({ type: 'history', conversationId, messages: page2.messages, hasMore: page2.hasMore })
      renderer.render(store)
    }
    loading = false
  }

  // Use IntersectionObserver to detect when the user scrolls to the top.
  // We observe the scroll container itself — when scrollTop < 40px, load more.
  const scrollEl = renderer.getScrollEl()
  if (!scrollEl) return

  // Wait 300ms before arming the scroll listener — the initial render scrolls
  // to the bottom, which briefly passes through scrollTop=0 and could trigger
  // a spurious load before the user actually scrolls up.
  let armed = false
  setTimeout(() => { armed = true }, 300)

  const onScroll = () => {
    if (!armed) return
    if (scrollEl.scrollTop < 80 && store.hasMoreHistory && !loading) {
      void loadOlder()
    }
  }
  scrollEl.addEventListener('scroll', onScroll, { passive: true })
  renderer.setScrollCleanup(() => scrollEl.removeEventListener('scroll', onScroll))
}
